import { useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, Pencil, Search, X } from 'lucide-react'
import { Loading, IconButton } from '../../kit'

// The picker behind การรักษา and นัดหมาย on the lab form.
//
// It replaced two native <select>s (2026-08-17, Ram: "the normal dropdown will not do").
// A visit is not identified by a date — a patient can have three on one day — so an
// <option> string was the wrong control: choosing the right one needs the doctor, the
// branch, what was done and what was billed, all at once. This renders each candidate
// as a full row and the CHOSEN one as that same row, so the form shows what was linked
// rather than a collapsed label.
//
// Shell only: `renderRow` draws the content, so the treatment and appointment rows can
// look nothing alike while the selection behaviour, search, empty/loading states and the
// "change, never clear" rule live in one place.
//
// ⚠️ `allowClear` is FALSE once a link is persisted upstream — POST /lab's first_notempty
// ignores the 0 an unlink would send, so a ล้าง button there would silently do nothing.
// See LabInfoEditor's header for the whole rule.
//
// Kit note: the kit's <SearchSelect> takes text options (label + one hint line), and
// these rows are rich (date · branch · doctor avatar · badges · remark), so this stays a
// local inline list (kit gap). It follows the kit where it can: the Field label style,
// kit Loading, and Esc closes the open list only — never the Drawer underneath.
const SEARCH_THRESHOLD = 6

export default function LinkPicker({
  label, required = false, items = [], value, onChange, loading = false,
  allowClear = false, placeholder = 'เลือก…', emptyText = 'ไม่มีข้อมูล',
  clearText = 'ไม่ผูก', searchOf, renderRow, disabled = false, disabledText = '',
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const searchRef = useRef(null)

  const chosen = items.find((it) => String(it.id) === String(value)) || null
  const showSearch = items.length > SEARCH_THRESHOLD

  const shown = useMemo(() => {
    const ql = q.trim().toLowerCase()
    if (!ql || !searchOf) return items
    return items.filter((it) => String(searchOf(it) || '').toLowerCase().includes(ql))
  }, [items, q, searchOf])

  function pick(id) { onChange(id); setOpen(false); setQ('') }
  function toggle() {
    if (disabled || loading) return
    setOpen((o) => {
      if (!o) setTimeout(() => searchRef.current?.focus(), 0)
      return !o
    })
  }

  // Esc while the list is open closes the list and stops there (the Drawer's own Esc
  // listens on window, which a stopped event never reaches).
  const onKeyDown = (e) => {
    if (e.key === 'Escape' && open) { e.stopPropagation(); e.preventDefault(); setOpen(false); setQ('') }
  }

  return (
    <div className="space-y-1.5" onKeyDown={onKeyDown}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12.5px] font-medium text-slate-600">
          {label}{required && <span className="text-rose-500 ml-0.5" aria-hidden>*</span>}
        </span>
        {/* Only offered while the picker is closed and something is chosen — an open
            list is already the way to change it, and two affordances for one act read
            as two different acts. */}
        {chosen && !open && !disabled && (
          <button type="button" onClick={toggle}
            className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-brand-700">
            <Pencil size={11} /> เปลี่ยน
          </button>
        )}
      </div>

      {/* ── the chosen record, or the invitation to choose one ── */}
      {!open && (
        chosen ? (
          <div className="rounded-xl border border-brand-200 bg-brand-50/40 px-3 py-2">
            {renderRow(chosen, { chosen: true })}
          </div>
        ) : (
          <button type="button" onClick={toggle} disabled={disabled || loading}
            className={`w-full rounded-xl border border-dashed px-3 py-2.5 text-[13.5px] flex items-center justify-between gap-2 transition ${
              disabled || loading
                ? 'border-slate-200 text-slate-300 cursor-default'
                : 'border-slate-300 text-slate-500 hover:border-brand-300 hover:bg-brand-50/40 hover:text-brand-700'
            }`}>
            {loading
              ? <Loading variant="inline" />
              : <span>{(disabled && disabledText) || placeholder}</span>}
            {!loading && !disabled && <ChevronDown size={14} className="shrink-0" />}
          </button>
        )
      )}

      {/* ── the list ── */}
      {open && (
        <div className="rounded-xl border border-brand-300 bg-white overflow-hidden">
          <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-slate-100 bg-slate-50/70">
            {showSearch ? (
              <>
                <Search size={13} className="text-slate-400 shrink-0" />
                <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="ค้นหา…"
                  className="flex-1 min-w-0 bg-transparent text-[13px] text-slate-700 outline-none placeholder:text-slate-400" />
              </>
            ) : (
              <span className="flex-1 text-[12px] text-slate-400">{items.length} รายการ</span>
            )}
            <IconButton icon={X} size="sm" label="ปิดรายการ (Esc)" onClick={() => { setOpen(false); setQ('') }} />
          </div>

          {/* Capped and scrollable: an ortho patient's visit list is long, and a picker
              that pushes the rest of the form off-screen is its own problem. */}
          <div className="max-h-[280px] overflow-y-auto divide-y divide-slate-50">
            {allowClear && (
              <button type="button" onClick={() => pick('')}
                className="w-full text-left px-3 py-2 text-[13px] text-slate-500 hover:bg-slate-50">
                {clearText}
              </button>
            )}
            {shown.length === 0 && (
              <div className="px-3 py-6 text-center text-[13px] text-slate-400">
                {items.length === 0 ? emptyText : 'ไม่พบรายการที่ค้นหา'}
              </div>
            )}
            {shown.map((it) => {
              const isSel = String(it.id) === String(value)
              return (
                <button key={it.id} type="button" onClick={() => pick(it.id)}
                  className={`w-full text-left px-3 py-2 flex items-start gap-2 transition ${isSel ? 'bg-brand-50' : 'hover:bg-slate-50'}`}>
                  <div className="min-w-0 flex-1">{renderRow(it, { chosen: false })}</div>
                  {isSel && <Check size={14} className="text-brand-600 shrink-0 mt-0.5" />}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
